import { useState } from 'react'

import { Box, Icon, Text, useToggle } from '@factorialco/deprecated-design-system'
import { F0Button } from '@factorialco/f0-react'
import { OneDateNavigator } from '@factorialco/f0-react/dist/experimental'
import moment from 'moment'

import { observer } from 'mobx-react'

import DayPicker from 'modules/core/components/DayPicker'
import PopoverMenu from 'modules/core/components/PopoverMenu'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import { stringToDate } from 'modules/core/lib/dates'
import { firstDayOfWeekAsIndex } from 'modules/core/lib/firstDayOfWeek'
import i18n from 'modules/core/lib/i18n'

import Selector from 'modules/shift_management/components/ShiftManagement/Content/TableHeader/CommandsFilterBar/ViewSelector/Selector'
import { viewTypeOptions } from 'modules/shift_management/components/ShiftManagement/Content/TableHeader/CommandsFilterBar/ViewSelector/viewTypeOptions'
import ViewOptionsSectionButtonOld from 'modules/shift_management/components/ShiftManagement/Content/TableHeader/ShiftActionsBar/ViewOptionsSectionButton/indexOld'
import useChangeView from 'modules/shift_management/hooks/useChangeView'
import { useEnabledFeatures } from 'modules/shift_management/hooks/useEnabledFeatures'
import useIsTimeFormat24H from 'modules/shift_management/hooks/useIsTimeFormat24H'
import { useShiftsRows } from 'modules/shift_management/hooks/useShiftsRows'
import { maxDate, minDate } from 'modules/shift_management/lib/getMinMaxDates'
import View from 'modules/shift_management/store/internal/models/View'
import { GranularityViewType } from 'modules/shift_management/store/internal/models/View'

interface Props {
  hasNoneShiftManagementPlanning: boolean
}

type DateGranularity = {
  granularity: string
  value: { from: Date; to: Date }
}

const NewThemeHeaderViewNavigator = ({ hasNoneShiftManagementPlanning }: Props) => {
  const { view, onChangePeriod, onChangeRoute } = useChangeView()
  const ALL_GRANULARITY_VIEWS: GranularityViewType[] = ['day', 'week', 'month']
  const [currentDate, setCurrentDate] = useState<DateGranularity>({
    granularity: 'week',
    value: { from: new Date(view.startAt), to: new Date(view.endAt) },
  })
  const [{ hasFeatureFlag }] = useEnabledFeatures()
  const newEntryFlowEnabled = hasFeatureFlag('dev_shift_management_autoscheduler_entry_flow')
  const hasMonthlyView = hasFeatureFlag('shift_management_monthly_view')
  const viewTypeReadonly = !hasMonthlyView || hasNoneShiftManagementPlanning

  const { access: currentUser } = useCurrentSession()
  const [{ shiftsRows }] = useShiftsRows()
  const canManageShifts = shiftsRows.some((shiftsRow) => shiftsRow.canManage)

  const [open, toggle] = useToggle(false)

  const { isTimeFormat24H } = useIsTimeFormat24H()

  const onClickBack = () => onChangePeriod(view.previousPeriod)
  const onClickForward = () => onChangePeriod(view.nextPeriod)

  const onClickToday = () => {
    const today = new View({
      date: moment(),
      viewType: view.viewType,
      groupType: view.groupType,
      isTimeFormat24H,
    })
    onChangePeriod(today)
  }

  const onClickSelect = (date: moment.Moment) => {
    const selectedView = new View({
      date,
      viewType: view.viewType,
      groupType: view.groupType,
      isTimeFormat24H,
    })
    onChangePeriod(selectedView)
    toggle(false)()
  }

  const onClickSelectNew = ({ granularity, value }: DateGranularity) => {
    // needed return as this function is called on every click of the date picker, but we only want it when the date changes
    if (
      value.from.getTime() === currentDate.value.from.getTime() &&
      granularity === currentDate.granularity
    )
      return
    const selectedView = new View({
      date: moment(value.from),
      viewType: view.viewType,
      groupType: view.groupType,
      isTimeFormat24H,
    })
    onChangePeriod(selectedView)
    setCurrentDate({
      granularity: granularity,
      value: { from: value.from, to: value.to },
    })
    toggle(false)()
  }

  const onViewChange = (value: string) => {
    onChangeRoute(`${value}/${view.groupType}`)
  }

  const borderStyle = { width: 's1', style: 'solid', color: 'grey400' } as const

  useUnsafeEffect(() => {
    // needed return as we update the view from outside this component, but we don't re render the component so we only want to update its value when updated outside it
    if (new Date(view.startAt).getDate() === currentDate.value.from.getDate()) return

    setCurrentDate((prev) => ({
      ...prev,
      value: { from: new Date(view.startAt), to: new Date(view.endAt) },
    }))
  }, [view.startAt, view.endAt])

  useUnsafeEffect(() => {
    onViewChange(currentDate.granularity)
  }, [currentDate.granularity])

  return (
    <Box flexDirection="row" alignItems="center" gap="s8" minWidth="none" justifyContent="center">
      {!newEntryFlowEnabled && (
        <F0Button
          onClick={onClickToday}
          label={i18n.t('common.today')}
          variant="outline"
          disabled={hasNoneShiftManagementPlanning}
        />
      )}

      {newEntryFlowEnabled && (
        <OneDateNavigator
          value={{
            granularity: currentDate.granularity,
            value: {
              from: new Date(view.startAt),
              to: new Date(view.endAt),
            },
          }}
          granularities={ALL_GRANULARITY_VIEWS}
          onSelect={(selected) => {
            const dateRange = selected?.value
            if (dateRange) {
              onClickSelectNew({
                granularity: selected.granularity,
                value: { from: dateRange.from, to: dateRange.to },
              })
            }
          }}
        />
      )}
      {!newEntryFlowEnabled && (
        <PopoverMenu type="dialog" onClick={toggle(!open)} open={open}>
          <Box flexDirection="row" minWidth="s200" height="s32">
            <Box
              width="s32"
              height="full"
              alignItems="center"
              justifyContent="center"
              border={{ all: borderStyle }}
              borderRadius={{ topLeft: 'abs008', bottomLeft: 'abs008' }}
              onClick={onClickBack}
              background="grey000White"
            >
              <Icon.Small icon="chevron-left" color="grey600" />
            </Box>

            <PopoverMenu.Trigger disabled={hasNoneShiftManagementPlanning}>
              <Box flexDirection="row" minWidth="s140" height="s32" background="grey000White">
                <Box
                  flexDirection="row"
                  flexGrow
                  paddingX="s8"
                  alignItems="center"
                  justifyContent="center"
                  gap="s4"
                  border={{ top: borderStyle, bottom: borderStyle }}
                >
                  <Text.Normal
                    weight="normal"
                    color={hasNoneShiftManagementPlanning ? 'grey600' : 'grey800'}
                    ellipsis
                  >
                    {view.headerTitle}
                  </Text.Normal>
                  <Icon.Small icon="chevron-down" color="grey600" />
                </Box>
              </Box>
            </PopoverMenu.Trigger>

            <Box
              width="s32"
              height="full"
              alignItems="center"
              justifyContent="center"
              border={{ all: borderStyle }}
              borderRadius={{ topRight: 'abs008', bottomRight: 'abs008' }}
              onClick={onClickForward}
              background="grey000White"
            >
              <Icon.Small icon="chevron-right" color="grey600" />
            </Box>
          </Box>

          <PopoverMenu.Content side="bottom" align="center" width="s360">
            <DayPicker
              label={i18n.t('common.select')}
              firstDayOfWeek={firstDayOfWeekAsIndex(currentUser.firstDayOfWeek)}
              onChange={(date: string) => onClickSelect(stringToDate(date, 'ui'))}
              minDate={minDate}
              maxDate={maxDate}
              showOnlyCalendar
            />
          </PopoverMenu.Content>
        </PopoverMenu>
      )}
      {!newEntryFlowEnabled && (
        <Selector
          value={view.viewType}
          options={viewTypeOptions(view.groupType)}
          onChange={onViewChange}
          readonly={viewTypeReadonly}
        />
      )}
      {!newEntryFlowEnabled && (
        <ViewOptionsSectionButtonOld
          disabled={hasNoneShiftManagementPlanning}
          canManageShifts={canManageShifts}
        />
      )}
    </Box>
  )
}

export default observer(NewThemeHeaderViewNavigator)
